import React, { useEffect, useState } from 'react';
import {
  MdHome,
  MdOutlineFormatAlignJustify,
  MdEdit,
  MdCircleNotifications,
  MdChat,
  MdFeedback,
  MdCheckCircle,
  MdSpeed,
  MdInfo,
  MdVisibility,
  MdDescription,
  MdSettings,
  MdExpandMore,
  MdExpandLess,
  MdAccountTree,
  MdCode,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdAssignment,
  MdDashboard,
  MdSchool,
  MdHelp,
  MdLogout,
  MdPerson,
  MdArrowBack,
  MdAnalytics,
  MdTimeline,
  MdAutoAwesome
} from "react-icons/md";

import { hasInstructorPermissions, canManageClasses } from '../utils/permissions';
import { useClass } from '../utils/ClassContext';

// UIC Color Constants
const UIC_COLORS = {
  primary: '#001f3f',    // Navy Blue
  secondary: '#cc0000',   // Red
  accent: '#ffffff',      // White
  text: '#333333',
  background: '#f8f9fa',
  lightBlue: '#0066cc',
  darkBlue: '#003366'
};

const UnifiedSidebar = ({
  username,
  userRole,
  userName,
  selectedPage,
  notificationCount,
  onNavigate,
  refreshUserStats,
  onLogout,
  isPublicView = false,
  isViewingAsStudent = false,
  onViewAsStudent = null,
  onExitStudentView = null
}) => {
  const { selectedClass, clearClass } = useClass();
  const [isERDExpanded, setIsERDExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Determine if we're in class mode or global mode
  const isInClassMode = selectedClass !== null;

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('unifiedSidebarCollapsed');
    if (saved !== null) {
      setIsCollapsed(JSON.parse(saved));
    }
  }, []);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('unifiedSidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Toggle collapse state
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Check if user has instructor-level permissions
  const hasAdminPermissions = hasInstructorPermissions(userRole);

  // Check if user is specifically an Instructor (not TA)
  const isInstructor = userRole === 'Instructor';
  const isStudent = userRole === 'Student';

  // Get tool settings from selected class
  const classTools = selectedClass?.tools || {};
  const isERDEditorEnabled = classTools.erd_editor?.enabled ?? true;
  const isDocumentEditorEnabled = classTools.document_editor?.enabled ?? true;

  // ERD-related items for the dropdown
  const erdItems = [
    {
      id: 'editor',
      label: 'ERD Editor',
      icon: MdEdit,
      description: 'Create and edit ERD diagrams'
    },
    {
      id: 'guidelines',
      label: 'ERD Guidelines',
      icon: MdOutlineFormatAlignJustify,
      description: 'ERD notation guidelines'
    }
  ];

  // Only add discussions if not public view
  if (!isPublicView) {
    erdItems.push({
      id: 'discussions',
      label: 'Discussions',
      icon: MdChat,
      description: 'Join conversations'
    });
  }

  // Add admin ERD items if user has permissions
  if (hasAdminPermissions) {
    erdItems.push(
      {
        id: 'environment-visibility',
        label: 'Environment Visibility',
        icon: MdVisibility,
        description: 'Control when students can see environments'
      },
      {
        id: 'feedback-log',
        label: 'Feedback Log',
        icon: MdFeedback,
        description: 'View all student feedback'
      },
      {
        id: 'erd-ai-guideline',
        label: 'AI Guideline',
        icon: MdAutoAwesome,
        description: 'ERD feedback rules sent to the LLM'
      }
    );
  }

  // Student-only items (in-class mode)
  const studentBottomItems = isStudent && isInClassMode ? [
    {
      id: 'my-quota',
      label: 'My Quota',
      icon: MdSpeed,
      description: 'View your feedback quota per environment'
    }
  ] : [];

  // Admin-only items for the bottom section
  const adminBottomItems = hasAdminPermissions ? [
    ...(isDocumentEditorEnabled ? [{
      id: 'activity-tracking',
      label: 'Activity Tracking',
      icon: MdAnalytics,
      description: 'View student activity and engagement with documents'
    }] : []),
    {
      id: 'consents',
      label: 'Show Consents',
      icon: MdCheckCircle,
      description: 'Manage consent forms'
    },
    {
      id: 'quota-management',
      label: 'Manage Quota',
      icon: MdSpeed,
      description: 'Adjust user quotas'
    },
    {
      id: 'users-info',
      label: 'Users Info',
      icon: MdInfo,
      description: 'View user information'
    }
  ] : [];

  // Global menu items
  const globalMenuItems = canManageClasses(userRole) ? [
    {
      id: 'class-management',
      label: 'Class Management',
      icon: MdSchool,
      description: 'Create, archive, and manage classes'
    }
  ] : [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: MdDashboard,
      description: 'Select a class to work in'
    },
    {
      id: 'notifications',
      label: 'Global Notifications',
      icon: MdCircleNotifications,
      description: 'Site-wide announcements',
      badge: notificationCount > 0 ? notificationCount : null
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: MdHelp,
      description: 'Get help using the system'
    },
    {
      id: 'about',
      label: 'About',
      icon: MdInfo,
      description: 'About AI Assist LMS'
    }
  ];

  // Handle navigation clicks
  const handleNavigation = (pageId) => {
    onNavigate(pageId);
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    clearClass();
    onNavigate('dashboard');
  };

  // Toggle ERD dropdown
  const toggleERDDropdown = () => {
    setIsERDExpanded(!isERDExpanded);
  };

  // Check if any ERD page is selected
  const isERDPageSelected = erdItems.some(item => item.id === selectedPage);

  // Auto-expand ERD dropdown if an ERD page is selected
  useEffect(() => {
    if (isERDPageSelected) {
      setIsERDExpanded(true);
    }
  }, [isERDPageSelected]);

  return (
    <div
      className={`flex flex-col h-full shadow-xl transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      style={{ backgroundColor: UIC_COLORS.primary }}
    >
      {/* Header Section */}
      <div className="p-4 border-b border-gray-600 min-h-[72px] flex items-center">
        <div className="flex items-center justify-between w-full">
          {!isCollapsed && (
            <div className="flex items-center flex-1">
              {isInClassMode ? (
                // Class Mode Header
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-white">
                    {selectedClass.class_name} ({selectedClass.class_code})
                  </h2>
                </div>
              ) : (
                // Global Mode Header
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-600 rounded-lg flex items-center justify-center">
                    <MdDashboard className="text-white text-base" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">AI Assist</h2>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Back to Dashboard Button - Only show in class mode */}
            {!isCollapsed && isInClassMode && !isPublicView && (
              <button
                onClick={handleBackToDashboard}
                className="text-white hover:text-gray-300 transition-colors duration-200 p-1 rounded"
                title="Back to Dashboard"
              >
                <MdArrowBack size={18} />
              </button>
            )}
            <button
              onClick={toggleCollapse}
              className="text-white hover:text-gray-300 transition-colors duration-200"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
            </button>
          </div>
        </div>
      </div>


      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgb(75 85 99) #001f3f' }}>
        <div className={`space-y-1 ${isCollapsed ? 'px-2' : 'px-3'}`}>
          {isInClassMode ? (
            // CLASS MODE NAVIGATION
            <>
              {/* Home Button */}
              <button
                onClick={() => handleNavigation('home')}
                className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'home'
                  ? 'bg-red-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                  }`}
                title="Main dashboard"
              >
                <MdHome className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                {!isCollapsed && (
                  <div className="flex-1">
                    <span className="text-sm font-medium">Home</span>
                  </div>
                )}
              </button>

              {/* Notifications Button - Hidden for public users */}
              {!isPublicView && (
                <div className="relative">
                  <button
                    onClick={() => handleNavigation('notifications')}
                    className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'notifications'
                      ? 'bg-red-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                    title="View your notifications"
                  >
                    <MdCircleNotifications className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-sm font-medium">Notifications</span>
                          {notificationCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                              {notificationCount}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </button>
                  {isCollapsed && notificationCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                  )}
                </div>
              )}

              {/* ERD Dropdown Section — hidden for students */}
              {isERDEditorEnabled && !isStudent && (
                <div className="mt-2">
                  <button
                    onClick={toggleERDDropdown}
                    className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${isERDPageSelected
                      ? 'bg-red-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                    title="ERD Tools and Features"
                  >
                    <MdAccountTree className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <div className="flex-1">
                          <span className="text-sm font-medium">ERD</span>
                        </div>
                        {isERDExpanded ? (
                          <MdExpandLess className="text-gray-300" />
                        ) : (
                          <MdExpandMore className="text-gray-300" />
                        )}
                      </>
                    )}
                  </button>

                  {/* ERD Dropdown Items */}
                  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isERDExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className={`mt-1 space-y-1 ${isCollapsed ? '' : 'ml-6'}`}>
                      {erdItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleNavigation(item.id)}
                          className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'} text-left rounded-lg transition-all duration-200 ${selectedPage === item.id
                            ? 'bg-blue-700 text-white'
                            : 'text-gray-400 hover:bg-blue-800 hover:text-white'
                            }`}
                          title={isCollapsed ? item.label : item.description}
                        >
                          <item.icon className={`${isCollapsed ? 'text-lg' : 'text-sm mr-3'}`} />
                          {!isCollapsed && (
                            <div className="flex-1">
                              <span className="text-xs font-medium">{item.label}</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Divider */}
              {!isCollapsed && (
                <div className="mt-4 mb-3">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-600"></div>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {isDocumentEditorEnabled && (
                <>
                  <button
                    onClick={() => handleNavigation('document-editor')}
                    className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'document-editor'
                      ? 'bg-red-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                    title="AI-powered document creation and editing"
                  >
                    <MdDescription className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                    {!isCollapsed && (
                      <div className="flex-1">
                        <span className="text-sm font-medium">Documents</span>
                      </div>
                    )}
                  </button>
                  {/* Doc Editor AI Guideline — instructors/admins only, not TAs */}
                  {!isStudent && userRole !== 'TA' && (
                    <button
                      onClick={() => handleNavigation('doc-editor-ai-guideline')}
                      className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-2 ml-0' : 'px-3 py-2 ml-6'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'doc-editor-ai-guideline'
                        ? 'bg-blue-700 text-white'
                        : 'text-gray-400 hover:bg-blue-800 hover:text-white'
                        }`}
                      title="Document editor AI prompt instructions"
                    >
                      <MdAutoAwesome className={`${isCollapsed ? 'text-lg' : 'text-sm mr-3'}`} />
                      {!isCollapsed && (
                        <span className="text-xs font-medium">AI Guideline</span>
                      )}
                    </button>
                  )}
                </>
              )}

              {/* Code Editor Section — hidden for students */}
              {!isStudent && (
                <>
                  <button
                    onClick={() => handleNavigation('code-editor')}
                    className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'code-editor'
                      ? 'bg-red-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                    title="Interactive code editor with syntax highlighting"
                  >
                    <MdCode className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                    {!isCollapsed && (
                      <div className="flex-1">
                        <span className="text-sm font-medium">Code Editor</span>
                      </div>
                    )}
                  </button>

                  {/* Assignments Section */}
                  <button
                    onClick={() => handleNavigation('assignments')}
                    className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === 'assignments'
                      ? 'bg-red-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                    title={hasAdminPermissions ? "Manage assignments and grading" : "View and complete assignments"}
                  >
                    <MdAssignment className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                    {!isCollapsed && (
                      <div className="flex-1">
                        <span className="text-sm font-medium">Assignments</span>
                      </div>
                    )}
                  </button>
                </>
              )}

              {/* Student Bottom Items (My Quota) */}
              {studentBottomItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === item.id
                    ? 'bg-red-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                    }`}
                  title={item.description}
                >
                  <item.icon className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                  {!isCollapsed && (
                    <div className="flex-1">
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  )}
                </button>
              ))}

              {/* Admin Section Divider */}
              {hasAdminPermissions && adminBottomItems.length > 0 && !isCollapsed && (
                <div className="mt-4 mb-3">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-600"></div>
                    <span className="px-3 text-gray-400 text-xs font-medium uppercase">
                      {userRole === 'Instructor' ? 'Instructor Tools' : 'TA Tools'}
                    </span>
                    <div className="flex-1 border-t border-gray-600"></div>
                  </div>
                </div>
              )}

              {/* Admin Bottom Items */}
              {adminBottomItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${selectedPage === item.id
                    ? 'bg-red-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                    }`}
                  title={item.description}
                >
                  <item.icon className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                  {!isCollapsed && (
                    <div className="flex-1">
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  )}
                </button>
              ))}
            </>
          ) : (
            // GLOBAL MODE NAVIGATION
            <>
              {globalMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = selectedPage === item.id;

                return (
                  <div key={item.id} className="relative">
                    <button
                      onClick={() => handleNavigation(item.id)}
                      className={`w-full group flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} text-left rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-red-600 text-white shadow-lg transform scale-105'
                          : 'text-gray-300 hover:bg-blue-800 hover:text-white hover:transform hover:scale-102'
                      }`}
                      title={isCollapsed ? item.label : item.description}
                    >
                      <Icon className={`${isCollapsed ? 'text-xl' : 'text-xl mr-3'}`} />
                      {!isCollapsed && (
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{item.label}</span>
                            {item.badge && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                                {item.badge}
                              </span>
                            )}
                            {isActive && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                        </div>
                      )}
                    </button>
                    {isCollapsed && item.badge && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </nav>

      {/* Footer Section */}
      <div className="border-t border-gray-600 p-4 min-h-[88px] group">
        {/* User Menu - shown for everyone, in both global and class mode */}
        <div className="group">
          {isCollapsed ? (
            <div className="space-y-2">
              <button
                className="w-full flex items-center justify-center px-2 py-2 rounded-lg text-left hover:bg-blue-800 transition-all duration-300 ease-in-out text-white"
                title={username}
              >
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <MdPerson className="text-gray-300 text-base" />
                </div>
              </button>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out group-hover:opacity-100 max-h-0 opacity-0 ${
                isInClassMode && isInstructor ? 'group-hover:max-h-48' : 'group-hover:max-h-32'
              }`}>
                <div className="space-y-2 pt-2">
                  {/* Class Settings - Only for Instructors in class mode */}
                  {isInClassMode && isInstructor && (
                    <button
                      onClick={() => handleNavigation('class-settings')}
                      className={`w-full flex items-center justify-center px-2 py-2 rounded-lg transition-colors ${
                        selectedPage === 'class-settings'
                          ? 'bg-red-600 text-white'
                          : 'text-gray-300 hover:bg-blue-800'
                      }`}
                      title="Class Settings"
                    >
                      <MdSettings className={`text-lg ${
                        selectedPage === 'class-settings' ? 'text-white' : 'text-gray-400'
                      }`} />
                    </button>
                  )}
                  {isInstructor && !isViewingAsStudent && onViewAsStudent && (
                    <button
                      onClick={onViewAsStudent}
                      className="w-full flex items-center justify-center px-2 py-2 rounded-lg hover:bg-amber-600 transition-colors text-amber-300"
                      title="View as Student"
                    >
                      <MdVisibility className="text-amber-400 text-lg" />
                    </button>
                  )}
                  {isViewingAsStudent && onExitStudentView && (
                    <button
                      onClick={onExitStudentView}
                      className="w-full flex items-center justify-center px-2 py-2 rounded-lg hover:bg-amber-600 transition-colors text-amber-300"
                      title="Exit Student View"
                    >
                      <MdVisibility className="text-amber-400 text-lg" />
                    </button>
                  )}
                  <button
                    onClick={() => handleNavigation('settings')}
                    className="w-full flex items-center justify-center px-2 py-2 rounded-lg hover:bg-blue-800 transition-colors text-gray-300"
                    title="Settings"
                  >
                    <MdSettings className="text-gray-400 text-lg" />
                  </button>
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center justify-center px-2 py-2 rounded-lg hover:bg-red-600 transition-colors text-red-400"
                    title="Logout"
                  >
                    <MdLogout className="text-red-500 text-lg" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <button
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-blue-800 transition-all duration-300 ease-in-out text-white"
              >
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <MdPerson className="text-gray-300 text-base" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-white">{username}</div>
                  <div className="text-xs text-gray-300 capitalize">{userRole}</div>
                </div>
                <MdExpandMore className="text-gray-300 group-hover:rotate-180 transition-transform duration-300" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out group-hover:opacity-100 max-h-0 opacity-0 ${
                isInClassMode && isInstructor ? 'group-hover:max-h-56' : 'group-hover:max-h-40'
              }`}>
                <div className="mt-2 space-y-1">
                  {/* Class Settings - Only for Instructors in class mode */}
                  {isInClassMode && isInstructor && (
                    <button
                      onClick={() => handleNavigation('class-settings')}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        selectedPage === 'class-settings'
                          ? 'bg-red-600 text-white'
                          : 'text-gray-300 hover:bg-blue-800'
                      }`}
                    >
                      <MdSettings className={selectedPage === 'class-settings' ? 'text-white' : 'text-gray-400'} />
                      <span className="text-sm">Class Settings</span>
                    </button>
                  )}
                  {isInstructor && !isViewingAsStudent && onViewAsStudent && (
                    <button
                      onClick={onViewAsStudent}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-amber-600 transition-colors text-amber-300"
                    >
                      <MdVisibility className="text-amber-400" />
                      <span className="text-sm">View as Student</span>
                    </button>
                  )}
                  {isViewingAsStudent && onExitStudentView && (
                    <button
                      onClick={onExitStudentView}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-amber-600 transition-colors text-amber-300"
                    >
                      <MdVisibility className="text-amber-400" />
                      <span className="text-sm">Exit Student View</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleNavigation('settings')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-blue-800 transition-colors text-gray-300"
                  >
                    <MdSettings className="text-gray-400" />
                    <span className="text-sm">Settings</span>
                  </button>
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-red-600 transition-colors text-red-400"
                  >
                    <MdLogout className="text-red-500" />
                    <span className="text-sm">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedSidebar;
